/* ============================================================
   Страница турнира: hero + табы Обзор / Группы / Сетка / Участники.
   Группы — только у FIFA (как в макете).
   ============================================================ */
import { useEffect, useState } from 'react';
import { useAppStore } from '../../store';
import { useFedStore, useL } from '../store';
import { useFedContent } from '../content';
import {
  FIFA_GROUPS, FIFA_BRACKET, FIFA_PLAYERS, CS2_BRACKET, CS2_TEAMS,
  FIFA_RULES_RU, FIFA_RULES_KZ, CS2_RULES_RU, CS2_RULES_KZ,
  flag, ST_COLOR, hasEntryFee, teamRegistrationOpen, tournamentPhase, registrationPending, registrationOpensLabel, tl,
} from '../data';
import { Ic } from '../icons';
import { Rv, Seg } from '../ui';
import { MATCH_CENTER_PUBLIC_ENABLED } from '../features';
import MlbbTournament from '../mlbb/MlbbTournament';
import NovaBracket from '../mlbb/NovaBracket';
import { hasNovaGroups } from '../mlbb/novaBracketEngine';
import MatchCenter from '../matches/MatchCenter';
import TournamentQrModal from '../components/TournamentQr';
import DotaPoster, { DotaPosterPreview } from '../components/DotaPoster';
import useTournamentClock from '../useTournamentClock';
import TournamentRegulation from '../components/TournamentRegulation';

function MetaTile({ l, v }) {
  return (
    <div className="rounded-xl border border-line" style={{ padding: '13px 15px', background: 'rgba(10,10,12,.5)' }}>
      <div className="font-mono uppercase text-fg-faint" style={{ fontSize: 9.5, letterSpacing: '.12em' }}>{l}</div>
      <div className="font-semibold text-fg" style={{ fontSize: 14.5, marginTop: 5 }}>{v}</div>
    </div>
  );
}

function GroupCard({ gp, L }) {
  const cols = '18px 1fr 22px 22px 22px 42px 26px';
  return (
    <Rv className="overflow-hidden rounded-2xl border border-line shadow-card" style={{ background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}>
      <div className="flex items-center gap-3 border-b border-line" style={{ padding: '14px 16px' }}>
        <span
          className="flex items-center justify-center font-display font-bold text-white rounded-[9px]"
          style={{ width: 34, height: 34, fontSize: 18, background: 'linear-gradient(140deg,var(--fed-accent),var(--fed-accent-deep))', boxShadow: '0 6px 16px -6px var(--fed-accent-glow)' }}
        >
          {gp.id}
        </span>
        <span className="font-mono uppercase text-fg-muted" style={{ fontSize: 11, letterSpacing: '.14em' }}>{L.tab_groups} {gp.id}</span>
      </div>
      <div className="grid font-mono uppercase text-fg-faint" style={{ gridTemplateColumns: cols, gap: 4, padding: '8px 16px 4px', fontSize: 9, letterSpacing: '.06em' }}>
        <span>#</span><span></span>
        <span className="text-center">В</span><span className="text-center">Н</span><span className="text-center">П</span>
        <span className="text-center">М</span><span className="text-center">О</span>
      </div>
      {gp.rows.map((r, i) => {
        const [name, country, cc, [w, d, l, gf, ga, pts]] = r;
        const barBg = i === 0
          ? 'linear-gradient(var(--fed-gold),var(--fed-gold-deep))'
          : i === 1
            ? 'linear-gradient(#3BA55D,#227940)'
            : 'transparent';
        return (
          <div key={name} className="relative grid items-center border-t border-dashed border-line" style={{ gridTemplateColumns: cols, gap: 4, padding: '7px 16px' }}>
            <span className="absolute rounded-[3px]" style={{ left: 0, top: 6, bottom: 6, width: 3, background: barBg }} />
            <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>{i + 1}</span>
            <span className="flex items-center gap-2 min-w-0">
              <img src={flag(cc)} alt="" className="flex-shrink-0 rounded-[3px]" style={{ width: 20, boxShadow: '0 1px 3px rgba(0,0,0,.5)' }} />
              <span className="min-w-0">
                <span className="block font-semibold text-fg truncate" style={{ fontSize: 12.5 }}>{name}</span>
                <span className="block font-mono uppercase text-fg-faint truncate" style={{ fontSize: 8.5, letterSpacing: '.06em' }}>{country}</span>
              </span>
            </span>
            <span className="text-center font-mono text-fg-muted" style={{ fontSize: 11.5 }}>{w}</span>
            <span className="text-center font-mono text-fg-muted" style={{ fontSize: 11.5 }}>{d}</span>
            <span className="text-center font-mono text-fg-muted" style={{ fontSize: 11.5 }}>{l}</span>
            <span className="text-center font-mono text-fg-faint" style={{ fontSize: 10.5 }}>{gf}:{ga}</span>
            <span className="text-center font-mono font-bold text-gold" style={{ fontSize: 12.5 }}>{pts}</span>
          </div>
        );
      })}
    </Rv>
  );
}

function BracketSide({ s, hasFlags }) {
  const win = !!s.win;
  const color = win ? 'var(--fed-gold)' : 'var(--fed-fg-soft)';
  const dim = s.win === undefined && (s.g === '' || s.g === undefined);
  return (
    <div className="flex items-center gap-[9px]" style={{ padding: '9px 11px', opacity: dim ? 0.5 : 1 }}>
      {hasFlags && s.cc ? (
        <img src={flag(s.cc)} alt="" className="flex-shrink-0 rounded-[3px]" style={{ width: 22, boxShadow: '0 1px 3px rgba(0,0,0,.5)' }} />
      ) : hasFlags ? (
        <span className="flex-shrink-0" style={{ width: 22 }} />
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block font-semibold truncate" style={{ fontSize: 14, color }}>{s.c || s.n}</span>
        {s.c ? (
          <span className="block font-mono uppercase text-fg-faint truncate" style={{ fontSize: 8.5, letterSpacing: '.06em' }}>{s.n}</span>
        ) : null}
      </span>
      <span className="font-display font-bold" style={{ fontSize: 17, color }}>{s.g}</span>
    </div>
  );
}

export default function FedTournament() {
  const { go, lang, tourId, tourTab, setTourTab, openApply, setAuthReturn } = useFedStore();
  const L = useL();
  const tournaments = useFedContent((s) => s.tournaments);
  useTournamentClock(tournaments);
  const user = useAppStore((s) => s.user);
  const myApplications = useAppStore((s) => s.myApplications);
  const fetchMyApplications = useAppStore((s) => s.fetchMyApplications);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => { fetchMyApplications(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = tournaments.find((t) => t.id === tourId) || tournaments[0];
  const isFifa = cur.hasData === 'fifa';
  const isCs = cur.hasData === 'cs';
  const isCsDiscipline = isCs || /counter\s*-?\s*strike|\bcs\s*2?\b/i.test(`${cur.disc || ''} ${cur.game || ''}`);
  const isMlbb = cur.hasData === 'mlbb';
  const isTeamTournament = cur.hasData === 'team';
  const isDota = cur.teamGame === 'dota2';
  const disciplineKey = isCsDiscipline ? 'cs2' : isMlbb ? 'mlbb' : (cur.disc || '').trim().toLowerCase();
  const supportsMatchCenter = MATCH_CENTER_PUBLIC_ENABLED
    && (disciplineKey === 'cs2' || disciplineKey === 'mlbb');
  const isMlbbQ2 = cur.id === 'mlbb-mca-quals2-2026';
  const stLabel = {
    done: L.st_done,
    live: L.st_live,
    soon: L.st_soon,
    registration: lang === 'kz' ? 'Тіркелу ашық' : 'Регистрация открыта',
    closed: lang === 'kz' ? 'Тіркелу жабық' : 'Регистрация закрыта',
  };

  useEffect(() => {
    if (cur.id === tourId && tourTab === 'matches' && !supportsMatchCenter) setTourTab('overview');
  }, [cur.id, tourId, tourTab, supportsMatchCenter, setTourTab]);

  const curMeta = [
    { l: L.lbl_date, v: tl(cur, 'date', lang) },
    { l: L.lbl_place, v: tl(cur, 'place', lang) },
    { l: L.lbl_format, v: tl(cur, 'format', lang) },
    { l: L.lbl_platform, v: cur.platform },
    { l: L.lbl_players, v: cur.players ? String(cur.players) : '—' },
    { l: L.lbl_prize, v: tl(cur, 'prize', lang) },
  ];

  const tabsAll = [
    ['overview', L.tab_overview],
    ...(supportsMatchCenter ? [['matches', lang === 'kz' ? 'Матчтар' : 'Матчи']] : []),
    ['groups', L.tab_groups],
    ['bracket', L.tab_bracket],
    ['players', L.tab_players],
  ];
  // контент, добавленный через админку (для новых турниров без статичных данных)
  // Ручные данные из админки всегда важнее встроенных исторических сидов.
  // Сиды FIFA/CS2 остаются резервом, пока администратор не ввёл замену.
  const customGroups = Array.isArray(cur.groups) && cur.groups.length > 0;
  const customRounds = Array.isArray(cur.rounds) && cur.rounds.length > 0;
  const customPlayers = Array.isArray(cur.playersList) && cur.playersList.length > 0;

  const tabs = tabsAll
    .filter(([k]) => isFifa || customGroups || k !== 'groups')
    .map(([k, label]) => ({ key: k, label, active: tourTab === k, onClick: () => setTourTab(k) }));

  // ===== заявка на участие =====
  const form = cur.form;
  const deadlinePassed = form?.deadline && new Date(form.deadline + 'T23:59:59') < new Date();
  const formOpen = !!(form && form.open && (form.fields || []).length && !deadlinePassed);
  const registrationOpen = isTeamTournament ? teamRegistrationOpen(cur) : formOpen;
  const myApp = myApplications.find((a) => a.tournament_key === cur.id);
  const isAdminUser = user && (user.user_type === 'admin' || user.role === 'admin');
  const displayPrize = cur.id === 'tdq0g2qy'
    ? '1 000 000 ₸'
    : tl(cur, 'prize', lang);
  const displayDate = cur.id === 'tdq0g2qy'
    ? (lang === 'kz' ? '2026 жылғы 3–4 қазан' : '3–4 октября 2026')
    : tl(cur, 'date', lang);

  const applyClick = () => {
    if (!user) {
      setAuthReturn({ route: 'tournament', extra: { tourId: cur.id, tourTab: 'overview' } });
      go('auth', { authMode: 'signup' });
      return;
    }
    openApply(cur.id);
  };

  const openTeamRegistration = () => {
    setTourTab('reg');
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      const target = document.getElementById('cs2-tournament-panel');
      if (!target) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    }, 80);
  };

  const customRules = cur[`rules_${lang}`] || cur.rules_ru;
  const rules = Array.isArray(customRules) && customRules.length
    ? customRules
    : isFifa ? (lang === 'ru' ? FIFA_RULES_RU : FIFA_RULES_KZ) : (lang === 'ru' ? CS2_RULES_RU : CS2_RULES_KZ);
  const bracket = isFifa ? FIFA_BRACKET : CS2_BRACKET;
  const hasChamp = cur.status === 'done';
  const hasBracketData = isFifa || isCs || customRounds;

  // раунды из админки {a,sa,b,sb} → формат отображения сетки
  const displayBracket = customRounds
    ? cur.rounds.map((r) => ({
        title_ru: r.title, title_kz: r.title,
        matches: (r.matches || []).map((m) => {
          const sa = parseInt(m.sa, 10);
          const sb = parseInt(m.sb, 10);
          const hasScore = !isNaN(sa) && !isNaN(sb) && sa !== sb;
          return {
            a: { n: m.a || '—', g: m.sa ?? '', win: hasScore && sa > sb },
            b: { n: m.b || '—', g: m.sb ?? '', win: hasScore && sb > sa },
          };
        }),
      }))
    : bracket;

  return (
    <div className="fed-pagewrap max-w-[1320px] mx-auto" style={{ padding: '28px 24px 72px' }}>
      {!isMlbbQ2 && <div className="tournament-top-actions"><button
        onClick={() => go('tournaments')}
        className="bg-transparent border-none cursor-pointer text-fg-muted font-display font-semibold uppercase hover:text-fg"
        style={{ fontSize: 14, letterSpacing: '.04em', padding: '6px 0' }}
      >
        {L.back_tours}
      </button>

      <div className="tournament-top-actions__right">
      {cur.qrEnabled && (
        <button type="button" onClick={() => setQrOpen(true)} className="tournament-qr-entry">
          <Ic name="grid" size={18} />
          {lang === 'kz' ? 'QR код' : 'Показать QR-код'}
        </button>
      )}
      {isTeamTournament && !isDota && registrationOpen && (
        <button type="button" onClick={openTeamRegistration} className="cs2-registration-entry">
          <Ic name="zap" size={18} />
          {lang === 'kz' ? 'Тіркелу' : 'Зарегистрироваться'}
          <Ic name="chevD" size={18} />
        </button>
      )}
      </div>
      </div>}

      {isMlbbQ2 && cur.qrEnabled && <button type="button" onClick={() => setQrOpen(true)} className="tournament-qr-entry tournament-qr-entry--standalone"><Ic name="grid" size={18} />{lang === 'kz' ? 'QR код' : 'Показать QR-код'}</button>}

      {/* ===== hero ===== */}
      {isMlbbQ2 ? (
        <Rv className="mlbb-q2-hero relative overflow-hidden rounded-[20px] border border-line-2 shadow-card">
          <img className="mlbb-q2-hero-desktop" src="/assets/млбб 1.png?v=1" alt="MCA League — Квалификации #2" />
          <button
            type="button"
            aria-label={L.back_tours}
            title={L.back_tours}
            onClick={() => go('tournaments')}
            className="mlbb-q2-back-hotspot"
          />
          <div
            className="mlbb-q2-hero-mobile"
            style={{ backgroundImage: `linear-gradient(180deg,rgba(5,5,8,.08),rgba(5,5,8,.2)),url('${encodeURI('/assets/млбб 1.png?v=1')}')` }}
          >
            <button onClick={() => go('tournaments')} className="mlbb-q2-back">← {L.back_tours}</button>
            <div className="mlbb-q2-pills">
              <span>MLBB</span>
              <span className="is-open"><i />{lang === 'kz' ? 'Тіркелу ашық' : 'Регистрация открыта'}</span>
            </div>
            <h1><em>MCA League</em>{lang === 'kz' ? 'Іріктеу #2' : 'Квалификации #2'}</h1>
            <div className="mlbb-q2-facts">
              <span><Ic name="pin" size={15} />{cur[`place_${lang}`] || cur.place_ru}</span>
              <span><Ic name="trophy" size={15} />{cur.slots || 2} {lang === 'kz' ? 'лигаға слот' : 'слота в MCA League'}</span>
            </div>
          </div>
        </Rv>
      ) : isDota ? (
        <Rv className={`dota-tour-hero${cur.image ? ' dota-tour-hero--poster' : ''}`}>
          <div className="dota-tour-hero__copy">
            <div className="dota-tour-hero__pills"><span>DOTA 2 · 5×5 LAN</span><span style={{ color: ST_COLOR[tournamentPhase(cur)] }}>{stLabel[tournamentPhase(cur)]}</span></div>
            <h1>{tl(cur, 'title', lang)}</h1>
            <div className="dota-tour-hero__facts">
              <span><Ic name="calendar" size={18} />{displayDate}</span>
              <span><Ic name="pin" size={18} />{tl(cur, 'place', lang)}</span>
              <span><Ic name="trophy" size={18} />{displayPrize}{hasEntryFee(cur) && tl(cur, 'fee', lang) && ` · ${tl(cur, 'fee', lang)}`}</span>
              <span><Ic name="community" size={18} />{lang === 'kz' ? 'Қазақстан тұрғындары, 14+ · 5 ойыншы + 1 қосалқы' : 'Жители Казахстана, 14+ · 5 игроков + 1 запасной'}</span>
            </div>
            {registrationPending(cur) && <div className="dota-tour-hero__notice">{registrationOpensLabel(cur, lang)}</div>}
            {registrationOpen && <button type="button" onClick={openTeamRegistration} className="dota-tour-hero__cta"><Ic name="zap" size={18} />{lang === 'kz' ? 'Команданы тіркеу' : 'Зарегистрировать команду'}<Ic name="chevD" size={18} /></button>}
          </div>
          <div className="dota-tour-hero__art">{cur.image ? (
            <DotaPosterPreview tour={cur} lang={lang} className="dota-poster-preview--hero" />
          ) : <DotaPoster tour={cur} lang={lang} />}</div>
        </Rv>
      ) : isCsDiscipline && cur.image ? (
        <Rv className={`cs2-tournament-hero${isTeamTournament ? ' is-team-art' : ''}`}>
          <div className={`cs2-tournament-hero__poster${isTeamTournament ? ' is-full-art' : ''}`}>
            <img src={isTeamTournament ? '/assets/5298879394677990360.jpg' : cur.image} alt={tl(cur, 'title', lang)} />
          </div>
          <div className="cs2-tournament-hero__copy">
            <div className="cs2-tournament-hero__pills">
              <span>Counter-Strike 2</span>
              {registrationOpen ? (
                <span className="is-open"><i />{lang === 'kz' ? 'Тіркелу ашық' : 'Регистрация открыта'}</span>
              ) : isTeamTournament ? (
                <span style={{ color: ST_COLOR[tournamentPhase(cur)] }}>{stLabel[tournamentPhase(cur)]}</span>
              ) : null}
            </div>
            <p>{lang === 'kz' ? 'СҚО ашық командалық турнирі' : 'Открытый командный турнир СКО'}</p>
            <h1>{tl(cur, 'title', lang)}</h1>
            <div className="cs2-tournament-hero__facts">
              <span><Ic name="calendar" size={17} />{displayDate}</span>
              <span><Ic name="pin" size={17} />{tl(cur, 'place', lang)}</span>
              <span><Ic name="trophy" size={17} />{displayPrize}</span>
            </div>
            {!isTeamTournament && formOpen && !isAdminUser && !myApp && (
              <button onClick={applyClick} className="cs2-tournament-hero__cta">
                <Ic name="zap" size={19} />{lang === 'kz' ? 'Тіркелу' : 'Зарегистрироваться'}<Ic name="chevR" size={18} />
              </button>
            )}
          </div>
        </Rv>
      ) : <Rv className="relative overflow-hidden rounded-[20px] border border-line-2 shadow-card" style={{ background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))', marginBottom: 24 }}>
        <div className="absolute inset-0" style={{ opacity: 0.55, background: `radial-gradient(ellipse at 88% -20%,${isMlbb ? 'rgba(168,85,247,.32)' : 'rgba(225,18,23,.28)'},transparent 58%)` }} />
        <div className="relative flex flex-col gap-5 p-8">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="font-display font-bold text-white rounded-[7px] border border-line" style={{ padding: '5px 11px', fontSize: 12, letterSpacing: '.08em', background: 'rgba(6,6,8,.5)' }}>{cur.disc}</span>
            {teamRegistrationOpen(cur) ? (
              <span className="inline-flex items-center gap-1.5 rounded-full font-mono uppercase" style={{ padding: '5px 11px', fontSize: 10.5, letterSpacing: '.1em', color: 'var(--fed-ok)', background: 'rgba(67,217,106,.14)', border: '1px solid rgba(67,217,106,.4)' }}>
                <span className="fed-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--fed-ok)', boxShadow: '0 0 8px var(--fed-ok)' }} />
                {lang === 'kz' ? 'Тіркелу ашық' : 'Регистрация открыта'}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full font-mono uppercase bg-surface-2 border border-line" style={{ padding: '5px 11px', fontSize: 10.5, letterSpacing: '.1em', color: ST_COLOR[tournamentPhase(cur)] }}>{stLabel[tournamentPhase(cur)]}</span>
            )}
            <span className="font-mono uppercase text-fg-muted" style={{ fontSize: 11, letterSpacing: '.14em' }}>{cur.game}</span>
          </div>
          <h1 style={{ fontSize: 'clamp(30px,6vw,68px)' }}>{cur.title}</h1>
          {/* MLBB: ключевые факты живут в карточке «Обзор» — в hero не дублируем
              6 плиток, показываем компактную строку */}
          {isMlbb ? (
            <div className="flex flex-wrap gap-x-5 gap-y-2.5">
              {[['calendar', cur.date_ru], ['globe', cur.place_ru], ['diamond', cur.prize_ru]].filter(([, v]) => v).map(([ic, v]) => (
                <span key={v} className="inline-flex items-center gap-2 text-fg-soft" style={{ fontSize: 13.5 }}>
                  <Ic name={ic} size={15} color="#C084FC" /> {v}
                </span>
              ))}
            </div>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
              {curMeta.map((m) => <MetaTile key={m.l} l={m.l} v={m.v} />)}
            </div>
          )}
        </div>
      </Rv>}

      {/* ===== MLBB: своя механика (регистрация, команды, сетка с чек-ином) ===== */}
      {isMlbb && <MlbbTournament key={cur.id} cur={cur} />}
      {isTeamTournament && <MlbbTournament key={cur.id} cur={cur} game={cur.teamGame || 'cs2'} />}

      {/* ===== tabs ===== */}
      {!isMlbb && !isTeamTournament && <>
      <Seg items={tabs} className="mb-6" minWidth={110} />
      <TournamentRegulation tournament={cur} lang={lang} />

      {/* ===== ОБЗОР ===== */}
      {tourTab === 'overview' && (
        <div className="fed-split-2">
          <Rv className="flex flex-col gap-5">
            <div className="rounded-2xl bg-surface border border-line" style={{ padding: 26 }}>
              <h3 className="mb-3" style={{ fontSize: 20 }}>{L.ov_about}</h3>
              <p className="text-fg-soft" style={{ fontSize: 15, lineHeight: 1.7 }}>{tl(cur, 'about', lang)}</p>
            </div>
            <div className="rounded-2xl bg-surface border border-line" style={{ padding: 26 }}>
              <h3 className="mb-3.5" style={{ fontSize: 20 }}>{L.ov_rules}</h3>
              <div className="flex flex-col">
                {rules.map((r, i) => (
                  <div key={i} className="flex gap-3 items-start border-b border-dashed border-line text-fg-soft" style={{ padding: '11px 0', fontSize: 14, lineHeight: 1.5 }}>
                    <span className="text-gold flex-shrink-0">▸</span>
                    <span>{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </Rv>

          <Rv delay={0.08}>
            {hasChamp && cur.champ ? (
              <div
                className="relative overflow-hidden rounded-2xl"
                style={{
                  padding: 28, marginBottom: 20,
                  background: 'linear-gradient(160deg,rgba(230,183,60,.14),var(--fed-surface) 55%)',
                  border: '1px solid rgba(230,183,60,.4)',
                  boxShadow: 'var(--fed-shadow),var(--fed-glow-gold)',
                }}
              >
                <div className="font-mono uppercase text-gold" style={{ fontSize: 10, letterSpacing: '.2em' }}>{L.ov_champ}</div>
                <div className="flex items-center gap-4 mt-4">
                  <Ic name="trophy" size={46} color="var(--fed-gold)" />
                  <div>
                    <div className="flex items-center gap-2.5">
                      {cur.champCc ? <img src={flag(cur.champCc)} alt="" className="rounded" style={{ width: 34, boxShadow: '0 2px 8px rgba(0,0,0,.5)' }} /> : null}
                      <span className="font-display font-bold text-fg" style={{ fontSize: 30 }}>{cur.champ}</span>
                    </div>
                    <div className="text-fg-muted" style={{ fontSize: 14, marginTop: 6 }}>{cur.champPlayer}</div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl bg-surface border border-line" style={{ padding: 24 }}>
              <div className="font-mono uppercase text-fg-muted" style={{ fontSize: 10, letterSpacing: '.2em', marginBottom: 14 }}>{L.ov_key}</div>
              <div className="flex flex-col gap-3">
                {curMeta.map((m) => (
                  <div key={m.l} className="flex justify-between gap-3 border-b border-dashed border-line" style={{ paddingBottom: 11 }}>
                    <span className="text-fg-muted" style={{ fontSize: 13 }}>{m.l}</span>
                    <span className="font-semibold text-fg text-right" style={{ fontSize: 13.5 }}>{m.v}</span>
                  </div>
                ))}
              </div>
              {/* Заявка: статус / кнопка подачи / приём закрыт */}
              {myApp && myApp.status !== 'rejected' ? (
                <div
                  className="flex items-center gap-2.5 rounded-[11px] justify-center"
                  style={{
                    marginTop: 18, padding: '13px 16px', fontSize: 13.5, fontWeight: 600,
                    color: myApp.status === 'verified' ? 'var(--fed-ok)' : 'var(--fed-gold)',
                    background: myApp.status === 'verified' ? 'rgba(67,217,106,.1)' : 'rgba(230,183,60,.1)',
                    border: `1px solid ${myApp.status === 'verified' ? 'rgba(67,217,106,.4)' : 'rgba(230,183,60,.35)'}`,
                  }}
                >
                  <Ic name={myApp.status === 'verified' ? 'check' : 'info'} size={16} />
                  {myApp.status === 'verified' ? L.ap_st_ok : L.ap_st_pending}
                </div>
              ) : (
                <>
                  {myApp && myApp.status === 'rejected' && (
                    <div
                      className="rounded-[11px]"
                      style={{
                        marginTop: 18, padding: '12px 16px', fontSize: 13,
                        color: 'var(--fed-accent-bright)', background: 'rgba(225,18,23,.08)',
                        border: '1px solid var(--fed-line-accent)',
                      }}
                    >
                      <div className="font-semibold">{L.ap_st_rej}</div>
                      {myApp.rejection_reason && (
                        <div className="text-fg-muted" style={{ fontSize: 12.5, marginTop: 4 }}>{myApp.rejection_reason}</div>
                      )}
                    </div>
                  )}
                  {formOpen && !isAdminUser && (
                    <button onClick={applyClick} className="fed-btn-primary w-full h-[46px] rounded-[11px]" style={{ marginTop: myApp ? 10 : 18, fontSize: 14.5 }}>
                      {myApp ? L.ap_again : L.tour_reg}
                    </button>
                  )}
                  {form && !formOpen && (
                    <div
                      className="rounded-[11px] text-center text-fg-muted"
                      style={{ marginTop: 18, padding: '13px 16px', fontSize: 13.5, border: '1px solid var(--fed-line-2)', background: 'var(--fed-surface-2)' }}
                    >
                      {L.ap_closed}
                    </div>
                  )}
                </>
              )}
            </div>
          </Rv>
        </div>
      )}

      {tourTab === 'matches' && supportsMatchCenter && <MatchCenter tournamentKey={cur.id} discipline={disciplineKey} />}

      {/* ===== ГРУППЫ (FIFA) ===== */}
      {tourTab === 'groups' && isFifa && !customGroups && (
        <div>
          <Rv className="flex gap-[18px] flex-wrap" style={{ marginBottom: 18 }}>
            <span className="inline-flex items-center gap-2 text-fg-muted" style={{ fontSize: 13 }}>
              <i className="rounded-[3px]" style={{ width: 11, height: 11, background: 'linear-gradient(var(--fed-gold),var(--fed-gold-deep))' }} />
              {L.grp_q1}
            </span>
            <span className="inline-flex items-center gap-2 text-fg-muted" style={{ fontSize: 13 }}>
              <i className="rounded-[3px]" style={{ width: 11, height: 11, background: 'linear-gradient(#3BA55D,#227940)' }} />
              {L.grp_q2}
            </span>
          </Rv>
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(310px,1fr))' }}>
            {FIFA_GROUPS.map((gp) => <GroupCard key={gp.id} gp={gp} L={L} />)}
          </div>
        </div>
      )}

      {/* ===== ГРУППЫ (добавленные через админку) ===== */}
      {tourTab === 'groups' && customGroups && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
          {cur.groups.map((gp) => (
            <Rv key={gp.id} className="overflow-hidden rounded-2xl border border-line shadow-card" style={{ background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}>
              <div className="flex items-center gap-3 border-b border-line" style={{ padding: '14px 16px' }}>
                <span
                  className="flex items-center justify-center font-display font-bold text-white rounded-[9px]"
                  style={{ width: 34, height: 34, fontSize: 18, background: 'linear-gradient(140deg,var(--fed-accent),var(--fed-accent-deep))', boxShadow: '0 6px 16px -6px var(--fed-accent-glow)' }}
                >
                  {gp.id}
                </span>
                <span className="font-mono uppercase text-fg-muted" style={{ fontSize: 11, letterSpacing: '.14em' }}>{L.tab_groups} {gp.id}</span>
              </div>
              {(gp.teams || []).map((t, i) => (
                <div key={i} className="flex items-center gap-3 border-t border-dashed border-line first:border-t-0" style={{ padding: '9px 16px' }}>
                  <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>{i + 1}</span>
                  <span className="font-semibold text-fg truncate" style={{ fontSize: 13.5 }}>{t}</span>
                </div>
              ))}
            </Rv>
          ))}
        </div>
      )}

      {/* ===== СЕТКА ===== */}
      {tourTab === 'bracket' && (
        hasNovaGroups(cur) ? <NovaBracket tour={cur} poll /> : hasBracketData ? (
          <div className="overflow-x-auto" style={{ paddingBottom: 12 }}>
            <div className="flex gap-6 items-start" style={{ width: 'max-content', minWidth: '100%' }}>
              {displayBracket.map((rd) => (
                <div key={rd.title_ru} className="flex flex-col gap-3.5 flex-1" style={{ minWidth: 230 }}>
                  <div className="text-center font-display font-bold uppercase text-fg border-b border-line" style={{ fontSize: 15, letterSpacing: '.06em', paddingBottom: 6 }}>
                    {rd['title_' + lang] || rd.title_ru}
                  </div>
                  <div className="flex flex-col gap-3.5 justify-around flex-1">
                    {rd.matches.map((m, i) => (
                      <Rv key={i} className="overflow-hidden rounded-xl border border-line-2 shadow-card" style={{ background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}>
                        {m.label ? (
                          <div className="font-mono uppercase text-gold border-b border-line" style={{ padding: '6px 10px', fontSize: 9, letterSpacing: '.14em', background: 'rgba(230,183,60,.05)' }}>
                            {m.label}
                          </div>
                        ) : null}
                        <BracketSide s={m.a} hasFlags={isFifa} />
                        <div className="border-t border-dashed border-line">
                          <BracketSide s={m.b} hasFlags={isFifa} />
                        </div>
                      </Rv>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Rv className="rounded-2xl bg-surface border border-line text-center" style={{ padding: '44px 24px' }}>
            <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 22 }}>{L.soon}</div>
            <p className="text-fg-muted mt-2" style={{ fontSize: 14 }}>{L.soonText}</p>
          </Rv>
        )
      )}

      {/* ===== УЧАСТНИКИ ===== */}
      {tourTab === 'players' && (
        customPlayers ? (
          <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))' }}>
            {cur.playersList.map((p, i) => (
              <Rv key={i} className="flex items-center gap-2.5 rounded-[11px] border border-line" style={{ padding: '11px 14px', background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}>
                <span className="font-mono text-fg-faint" style={{ fontSize: 10, width: 20 }}>{i + 1}</span>
                <span className="font-semibold text-fg truncate" style={{ fontSize: 13.5 }}>{p}</span>
              </Rv>
            ))}
          </div>
        ) : isFifa ? (
          <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))' }}>
            {FIFA_PLAYERS.map((p) => (
              <Rv key={p.num} className="grid items-center rounded-[11px] border border-line" style={{ gridTemplateColumns: '26px 28px 1fr 30px', gap: 10, padding: '11px 14px', background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}>
                <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>{p.num}</span>
                <img src={flag(p.cc)} alt="" className="rounded-[3px]" style={{ width: 24, boxShadow: '0 1px 3px rgba(0,0,0,.5)' }} />
                <span className="min-w-0">
                  <span className="block font-semibold text-fg truncate" style={{ fontSize: 13.5 }}>{p.n}</span>
                  <span className="block font-mono uppercase text-fg-faint" style={{ fontSize: 8.5, letterSpacing: '.08em' }}>{p.c}</span>
                </span>
                <span className="flex items-center justify-center font-display font-bold text-accent-bright rounded-lg" style={{ width: 30, height: 30, fontSize: 14, background: 'rgba(225,18,23,.1)', border: '1px solid var(--fed-line-accent)' }}>
                  {p.g}
                </span>
              </Rv>
            ))}
          </div>
        ) : isCs ? (
          <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))' }}>
            {CS2_TEAMS.map((t, i) => (
              <Rv key={t[0]} className="flex items-center gap-[13px] rounded-xl border border-line" style={{ padding: 14, background: 'linear-gradient(160deg,var(--fed-surface-2),var(--fed-surface))' }}>
                <span className="flex-shrink-0 flex items-center justify-center font-display font-bold text-fg rounded-[11px] border border-line" style={{ width: 42, height: 42, fontSize: 13, background: 'linear-gradient(150deg,var(--fed-surface-3),var(--fed-elevated))' }}>
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="font-semibold text-fg truncate" style={{ fontSize: 15 }}>{t[0]}</div>
                  <div className="font-mono text-fg-muted" style={{ fontSize: 11, marginTop: 2 }}>кап. {t[1]}</div>
                </div>
              </Rv>
            ))}
          </div>
        ) : (
          <Rv className="rounded-2xl bg-surface border border-line text-center" style={{ padding: '44px 24px' }}>
            <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 22 }}>{L.soon}</div>
            <p className="text-fg-muted mt-2" style={{ fontSize: 14 }}>{L.soonText}</p>
          </Rv>
        )
      )}
      </>}

      {/* ===== Вопросы по турниру ===== */}
      <Rv delay={0.05} className="flex items-center gap-4 flex-wrap rounded-2xl bg-surface border border-line" style={{ padding: '18px 22px', marginTop: 28 }}>
        <span className="flex-shrink-0 flex items-center justify-center rounded-[11px] text-accent-bright" style={{ width: 46, height: 46, background: 'rgba(225,18,23,.09)', border: '1px solid var(--fed-line-accent)' }}>
          <Ic name="phone" size={22} />
        </span>
        <div className="min-w-0">
          <div className="font-semibold text-fg" style={{ fontSize: 15 }}>
            {lang === 'kz' ? 'Турнир бойынша сұрақтар' : 'Вопросы по турниру'}
          </div>
          <div className="text-fg-muted" style={{ fontSize: 13.5, marginTop: 3 }}>
            {lang === 'kz' ? 'WhatsApp арқылы жазыңыз' : 'Напишите нам в WhatsApp'}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2" style={{ marginTop: 8 }}>
            <a
              href="https://wa.me/77787671112?text=Здравствуйте%21%20У%20меня%20есть%20вопрос%20по%20турниру."
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-accent-bright no-underline hover:underline"
              aria-label="Написать на номер +7 778 767 1112 в WhatsApp"
            >
              WhatsApp: +7 778 767 1112
            </a>
          </div>
        </div>
      </Rv>
      <TournamentQrModal open={qrOpen} onClose={() => setQrOpen(false)} tourId={cur.id} title={tl(cur, 'title', lang)} lang={lang} />
    </div>
  );
}
